'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Section {
  id: string;
  label: string;
  labelEs: string;
  scrollTarget: string;
  conversionStage: string;
  trackingEvent: string;
}

interface SectionAnchorNavProps {
  currentLanguage?: 'en' | 'es';
  onSectionChange?: (sectionId: string) => void;
  className?: string;
}

const SectionAnchorNav = ({ 
  currentLanguage = 'en', 
  onSectionChange,
  className = '' 
}: SectionAnchorNavProps) => {
  const [activeSection, setActiveSection] = useState('calculator');
  const [isSticky, setIsSticky] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(80);
  const [navHeight, setNavHeight] = useState(56);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Hides this bar while the user is actively scrolling down through content
  // (mirrors the main StickyHeaderNav's own hide/show behavior) — without
  // this, the bar sits fixed on top of whatever section happens to be
  // passing underneath it, covering headings, CTA buttons and filter pills
  // mid-scroll throughout the page, not just at anchor-jump targets.
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navRef = useRef<HTMLElement>(null);
  // Combined height of the fixed main header + this sub-nav — both are
  // stacked fixed bars, so any scroll target needs to clear both of them,
  // not just the main header (that mismatch was covering section headings
  // and buttons whenever this nav was visible).
  const totalOffset = headerHeight + navHeight;

  const sections: Section[] = [
    {
      id: 'calculator',
      label: 'Revenue Calculator',
      labelEs: 'Calculadora de Ingresos',
      scrollTarget: 'calculator',
      conversionStage: 'awareness',
      trackingEvent: 'nav_calculator_click'
    },
    {
      id: 'solution',
      label: 'AI Solution',
      labelEs: 'Solución IA',
      scrollTarget: 'solution-pillars',
      conversionStage: 'interest',
      trackingEvent: 'nav_solution_click'
    },
    {
      id: 'success',
      label: 'ROI Scenarios',
      labelEs: 'Escenarios de ROI',
      scrollTarget: 'beta-validation-results',
      conversionStage: 'consideration',
      trackingEvent: 'nav_success_click'
    },
    {
      id: 'implementation',
      label: 'How It Works',
      labelEs: 'Cómo Funciona',
      scrollTarget: 'implementation-process',
      conversionStage: 'evaluation',
      trackingEvent: 'nav_implementation_click'
    },
    {
      id: 'comparison',
      label: 'Features',
      labelEs: 'Características',
      scrollTarget: 'comparison',
      conversionStage: 'comparison',
      trackingEvent: 'nav_features_click'
    },
    {
      id: 'get-started',
      label: 'Get Started',
      labelEs: 'Comenzar',
      scrollTarget: 'get-started',
      conversionStage: 'conversion',
      trackingEvent: 'nav_get_started_click'
    }
  ];

  useEffect(() => {
    // Calculate header height dynamically
    const calculateHeaderHeight = () => {
      const headerElement = document.querySelector('header');
      if (headerElement) {
        setHeaderHeight(headerElement.offsetHeight);
      }
      if (navRef.current) {
        setNavHeight(navRef.current.offsetHeight);
      }
    };

    calculateHeaderHeight();
    window.addEventListener('resize', calculateHeaderHeight);

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroHeight = window.innerHeight * 0.9; // Adjust hero detection

      // Only show sticky nav when past the hero section and main header is visible
      const shouldBeSticky = scrollPosition > heroHeight && scrollPosition < document.documentElement.scrollHeight - window.innerHeight - 200;
      setIsSticky(shouldBeSticky);
      // Re-measure once the nav actually mounts, so totalOffset is correct
      // on the very first frame it becomes visible, not one scroll tick later.
      if (shouldBeSticky && navRef.current) {
        setNavHeight(navRef.current.offsetHeight);
      }

      // Hide on scroll-down (content is passing underneath, nothing to
      // navigate to right now), show on scroll-up or near the top of the
      // sticky range (user is likely orienting or about to use the nav).
      if (scrollPosition > lastScrollY && scrollPosition > heroHeight + 40) {
        setShowNav(false);
      } else {
        setShowNav(true);
      }
      setLastScrollY(scrollPosition);

      // Update active section based on scroll position
      const sectionElements = sections.map(section => ({
        ...section,
        element: document.getElementById(section.scrollTarget)
      }));

      const currentSection = sectionElements.find(section => {
        if (!section.element) return false;
        const rect = section.element.getBoundingClientRect();
        const offset = totalOffset + 10; // Account for both fixed bars
        return rect.top <= offset && rect.bottom >= offset;
      });

      if (currentSection && currentSection.id !== activeSection) {
        setActiveSection(currentSection.id);
        onSectionChange?.(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateHeaderHeight);
    };
  }, [activeSection, onSectionChange, totalOffset, lastScrollY]);

  // Every anchor target gets scroll-margin-top matching both fixed bars
  // combined, so natural scrolling and browser anchor jumps clear them too —
  // not just clicks handled by scrollToSection below.
  useEffect(() => {
    const styleId = 'section-anchor-nav-offset';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    const selectors = sections.map((s) => `#${s.scrollTarget}`).join(', ');
    styleEl.textContent = `${selectors} { scroll-margin-top: ${totalOffset + 12}px; }`;
  }, [totalOffset]);

  const scrollToSection = (section: Section) => {
    const element = document.getElementById(section.scrollTarget);
    if (element) {
      const offset = totalOffset + 12; // Clear both the header and this nav
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setActiveSection(section.id);
      setIsMobileMenuOpen(false);
      onSectionChange?.(section.id);
    }
  };

  // Don't render if not sticky to avoid double navigation
  if (!isSticky) {
    return null;
  }

  return (
    <nav data-component-id="src/components/common/SectionAnchorNav.tsx:199:4" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="199" data-component-end-line="263" data-component-file="SectionAnchorNav.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
      ref={navRef}
      className={`fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200 transition-all duration-300 ease-in-out ${
        showNav ? 'translate-y-0' : '-translate-y-full'
      } ${className}`}
      style={{ top: `${headerHeight}px` }}
    >
      <div data-component-id="src/components/common/SectionAnchorNav.tsx:206:6" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="206" data-component-end-line="262" data-component-file="SectionAnchorNav.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Navigation */}
        <div data-component-id="src/components/common/SectionAnchorNav.tsx:208:8" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="208" data-component-end-line="224" data-component-file="SectionAnchorNav.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-center%20justify-center%20py-3%22%7D" className="hidden md:flex items-center justify-center py-3">
          <div data-component-id="src/components/common/SectionAnchorNav.tsx:209:10" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="209" data-component-end-line="223" data-component-file="SectionAnchorNav.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20lg%3Aspace-x-4%20overflow-x-auto%22%7D" className="flex items-center space-x-2 lg:space-x-4 overflow-x-auto">
            {sections.map((section) => (
              <button data-component-id="src/components/common/SectionAnchorNav.tsx:211:14" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="211" data-component-end-line="221" data-component-file="SectionAnchorNav.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={section.id}
                onClick={() => scrollToSection(section)}
                className={`px-3 lg:px-4 py-2 rounded-lg font-medium text-sm lg:text-base transition-all duration-200 whitespace-nowrap ${
                  activeSection === section.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                }`}
              >
                {currentLanguage === 'en' ? section.label : section.labelEs}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div data-component-id="src/components/common/SectionAnchorNav.tsx:227:8" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="227" data-component-end-line="261" data-component-file="SectionAnchorNav.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Ahidden%20py-3%22%7D" className="md:hidden py-3">
          <button data-component-id="src/components/common/SectionAnchorNav.tsx:228:10" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="228" data-component-end-line="240" data-component-file="SectionAnchorNav.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20w-full%20px-4%20py-3%20bg-white%20rounded-lg%20border%20border-gray-200%20shadow-sm%22%7D"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-between w-full px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm"
          >
            <span data-component-id="src/components/common/SectionAnchorNav.tsx:232:12" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="232" data-component-end-line="234" data-component-file="SectionAnchorNav.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%20text-gray-900%20text-sm%22%7D" className="font-medium text-gray-900 text-sm">
              {sections.find(s => s.id === activeSection)?.[currentLanguage === 'en' ? 'label' : 'labelEs']}
            </span>
            <Icon data-component-id="src/components/common/SectionAnchorNav.tsx:235:12" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="235" data-component-end-line="239" data-component-file="SectionAnchorNav.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-gray-500%22%7D" 
              name={isMobileMenuOpen ? "ChevronUpIcon" : "ChevronDownIcon"} 
              size={20} 
              className="text-gray-500" 
            />
          </button>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div data-component-id="src/components/common/SectionAnchorNav.tsx:244:12" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="244" data-component-end-line="259" data-component-file="SectionAnchorNav.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20left-4%20right-4%20mt-2%20bg-white%20rounded-lg%20border%20border-gray-200%20shadow-lg%20z-50%22%7D" className="absolute left-4 right-4 mt-2 bg-white rounded-lg border border-gray-200 shadow-lg z-50">
              <div data-component-id="src/components/common/SectionAnchorNav.tsx:245:14" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="245" data-component-end-line="258" data-component-file="SectionAnchorNav.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-2%20max-h-60%20overflow-y-auto%22%7D" className="py-2 max-h-60 overflow-y-auto">
                {sections.map((section) => (
                  <button data-component-id="src/components/common/SectionAnchorNav.tsx:247:18" data-component-path="src/components/common/SectionAnchorNav.tsx" data-component-line="247" data-component-end-line="256" data-component-file="SectionAnchorNav.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                    key={section.id}
                    onClick={() => scrollToSection(section)}
                    className={`w-full text-left px-4 py-3 transition-colors duration-200 text-sm ${
                      activeSection === section.id
                        ? 'bg-primary text-white' :'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {currentLanguage === 'en' ? section.label : section.labelEs}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default SectionAnchorNav;